import React from "react";
import Page from "./../../components/Page";
import Section from "./../../components/Section";
import SEO from "./../../components/SEO";

import Title, { SubTitle } from "../../components/releaseNotes/Title";
import Header from "../../components/releaseNotes/Header";
import Feature from "../../components/releaseNotes/Feature";
import MarkdownRenderer from "../../components/MarkdownRenderer";

// Import images for 3.13 features
import smartScrollbar from "../../images/3p13/smart-scrollbar.gif";
import studyList from "../../images/3p13/study-list.gif";
import appearance from "../../images/3p13/appearance.gif";
import labelmap from "../../images/3p13/labelmap.gif";
import bento from "../../images/3p13/bento-3p13.png";
import cornerstone5 from "../../images/3p13/cornerstone5.png";

const ChangeLog = `
* feat(segmentation): replace One Click Segment with ClickSegmentTool ([PR#6135](https://github.com/OHIF/Viewers/pull/6135))
* feat: Add extensibility for tmtv and segmentation modes ([PR#6128](https://github.com/OHIF/Viewers/pull/6128))
* feat(next): native Generic Viewport migration (useNextViewports) ([PR#6101](https://github.com/OHIF/Viewers/pull/6101))
* feat(ui-next): Adds appearance dialog with theme presets and custom theme support ([PR#6041](https://github.com/OHIF/Viewers/pull/6041))
* feat: Update to use pnpm ([PR#6031](https://github.com/OHIF/Viewers/pull/6031))
* feat(llm): add instructions md file for claude and other LLM tools ([PR#6017](https://github.com/OHIF/Viewers/pull/6017))
* feat(WorkListUINext): New Study List (WorkListUINext) ([PR#6005](https://github.com/OHIF/Viewers/pull/6005))
* feat(tests): Add tests for duplicating contour segments ([PR#6002](https://github.com/OHIF/Viewers/pull/6002))
* feat(testing): OHIF Test Agent Skills ([PR#5993](https://github.com/OHIF/Viewers/pull/5993))
* feat: Add customization URL parameter ([PR#5992](https://github.com/OHIF/Viewers/pull/5992))
* feat(slice scrollbar): Integrate ViewportSliceProgressScrollbar with customizations and docs ([PR#5960](https://github.com/OHIF/Viewers/pull/5960))
* feat(component): Adds SmartScrollbar to ui-next - OHIF-2558 ([PR#5924](https://github.com/OHIF/Viewers/pull/5924))
* feat(ui-next): Adds toggle state for ToolButton and Crosshair example ([PR#5914](https://github.com/OHIF/Viewers/pull/5914))
* feat(ecg): add DICOM ECG waveform extension ([PR#5856](https://github.com/OHIF/Viewers/pull/5856))
* feat: Add support for labelmap seg images in any supported tsuid (also for compressed bitmap) ([PR#5806](https://github.com/OHIF/Viewers/pull/5806))
* fix(security): update dependencies to patch CVEs ([PR#6169](https://github.com/OHIF/Viewers/pull/6169))
* fix(cornerstone): bump cornerstone3D to 5.6.8 ([PR#6165](https://github.com/OHIF/Viewers/pull/6165))
* fix(worklist): disable supportsFuzzyMatching on staticWado AWS sources so patient-name search doesn't return empty ([PR#6163](https://github.com/OHIF/Viewers/pull/6163))
* fix(cornerstone): bump cornerstone3D to 5.6.7 ([PR#6161](https://github.com/OHIF/Viewers/pull/6161))
* fix(crosshairs): keep Crosshairs active alongside brush/zoom/pan in MPR ([PR#6157](https://github.com/OHIF/Viewers/pull/6157))
* fix(deps): pin websocket-driver to 0.7.5 ([PR#6156](https://github.com/OHIF/Viewers/pull/6156))
* fix(cornerstone): bump cornerstone3D to 5.6.4 for mobile volume rendering ([PR#6155](https://github.com/OHIF/Viewers/pull/6155))
* fix(dynamic-volume): restore kinetic analysis and segment stats on 4D volumes ([PR#6149](https://github.com/OHIF/Viewers/pull/6149))
* fix(onboarding): use shepherd.js 15 directly to fix tour positioning crash ([PR#6148](https://github.com/OHIF/Viewers/pull/6148))
* fix(cli): link and unlink extensions/modes with pnpm instead of yarn ([PR#6144](https://github.com/OHIF/Viewers/pull/6144))
* fix(ui): clean up remaining legacy ui usage ([PR#6140](https://github.com/OHIF/Viewers/pull/6140))
* fix(app): appearance modal provider scope, worklist preview persistence, and tag browser label overflow ([PR#6136](https://github.com/OHIF/Viewers/pull/6136))
* fix: Several new worklist issues ([PR#6130](https://github.com/OHIF/Viewers/pull/6130))
* fix(security): ignore decompress security vulnerability ([PR#6125](https://github.com/OHIF/Viewers/pull/6125))
* fix: recipe config cleanup, report-only CSP, logout redirect validation ([PR#6124](https://github.com/OHIF/Viewers/pull/6124))
* fix(test): stabilize DICOM Tag Browser scrollbar screenshot ([PR#6118](https://github.com/OHIF/Viewers/pull/6118))
* fix(ci): deploy docs with Netlify CLI monorepo filter ([PR#6115](https://github.com/OHIF/Viewers/pull/6115))
* fix(wsi): mount WSI/SM viewports via setDisplaySets ([PR#6107](https://github.com/OHIF/Viewers/pull/6107))
* fix(hotkeyBindings): consolidate Escape hotkey behavior for contour drawing tools ([PR#6104](https://github.com/OHIF/Viewers/pull/6104))
* fix(pt): resolve Philips PET private SUV bulkdata before scaling ([PR#6096](https://github.com/OHIF/Viewers/pull/6096))
* fix: Release deploy of docs ([PR#6095](https://github.com/OHIF/Viewers/pull/6095))
* fix: pnpm related dependency bugs - nothing funcitonal ([PR#6094](https://github.com/OHIF/Viewers/pull/6094))
* fix(security): Update dependencies to fix security vulnerabilities. ([PR#6085](https://github.com/OHIF/Viewers/pull/6085))
* fix(docs): remove stray tool-call tags breaking the MDX build ([PR#6081](https://github.com/OHIF/Viewers/pull/6081))
* fix: pnpm deploy bugs for docs and viewer-dev ([PR#6077](https://github.com/OHIF/Viewers/pull/6077))
* fix: Fix the deployment to viewer-dev caused by missing .npmrc ([PR#6076](https://github.com/OHIF/Viewers/pull/6076))
* fix(security): Patch axios vulnerabilities. ([PR#6063](https://github.com/OHIF/Viewers/pull/6063))
* fix(security): Add tmp as a resolution to fix security vulnerability. ([PR#6044](https://github.com/OHIF/Viewers/pull/6044))
* fix: ohif tests to run with cornerstone 3d 5.0 ([PR#6043](https://github.com/OHIF/Viewers/pull/6043))
* fix(colors): Replace hardcoded colors with theme tokens ([PR#6040](https://github.com/OHIF/Viewers/pull/6040))
* fix(segmentation): restore navigation for duplicated contour segments ([PR#6038](https://github.com/OHIF/Viewers/pull/6038))
* fix(security): Update various dependencies to fix security vulnerabilities. ([PR#6023](https://github.com/OHIF/Viewers/pull/6023))
* fix(seg): prevent viewport orientation change when loading SEG in manual grid layout ([PR#6021](https://github.com/OHIF/Viewers/pull/6021))
* fix: remove waitForVolumeLoad from main toolbar page object ([PR#6004](https://github.com/OHIF/Viewers/pull/6004))
* fix(security): Patch axios security vulnerabilities. ([PR#5998](https://github.com/OHIF/Viewers/pull/5998))
* Fix: Segmentation Hide All fails when switching from 2D to 3D fourUp ([PR#5995](https://github.com/OHIF/Viewers/pull/5995))
* fix(measurement-tracking): restore tracked state on undo after Delete all ([PR#5994](https://github.com/OHIF/Viewers/pull/5994))
* fix(ToolGroupService): Centralize tool binding persistence and provide API to add and remove persisted bindings. ([PR#5989](https://github.com/OHIF/Viewers/pull/5989))
* fix(config url): Hardening fetch options. ([PR#5985](https://github.com/OHIF/Viewers/pull/5985))
* fix(security): Patch protobufjs for CVE-2026-41242. ([PR#5974](https://github.com/OHIF/Viewers/pull/5974))
* fix(security): refine dynamic config URL trust policy and document trusted-origin behavior ([PR#5973](https://github.com/OHIF/Viewers/pull/5973))
* fix(seg): prevent segmentations from spreading to all viewports before hydration confirmation in 3D four-up ([PR#5967](https://github.com/OHIF/Viewers/pull/5967))
* fix(MetadataProvider): Correctly assign imageId for multiframe images and remove unused frame information retrieval method ([PR#5965](https://github.com/OHIF/Viewers/pull/5965))
* fix(configuration): Harden dynamic datasource URL trust boundaries and credential handling. ([PR#5963](https://github.com/OHIF/Viewers/pull/5963))
* fix: Enhance multiframe instance handling and improve instance valida… ([PR#5956](https://github.com/OHIF/Viewers/pull/5956))
* fix: ignore auth in git ([PR#5955](https://github.com/OHIF/Viewers/pull/5955))
* fix(cornerstone): read FrameOfReferenceUID from display set in viewport service ([PR#5950](https://github.com/OHIF/Viewers/pull/5950))
* fix: A couple of changes to enable cs3d integration build ([PR#5944](https://github.com/OHIF/Viewers/pull/5944))
* fix(security): Update yarn.lock that was missed in PR #5936. ([PR#5940](https://github.com/OHIF/Viewers/pull/5940))
* fix(security): update dependencies to fix security vulnerabilities ([PR#5936](https://github.com/OHIF/Viewers/pull/5936))
* fix(defaultRouteInit): pass sorted display sets to hanging protocol for deterministic viewport order ([PR#5933](https://github.com/OHIF/Viewers/pull/5933))
* fix: remove thresholdRange label in double-range tool settings ([PR#5931](https://github.com/OHIF/Viewers/pull/5931))
* fix: prevent black viewport when navigating series with client-created segmentation ([PR#5919](https://github.com/OHIF/Viewers/pull/5919))
* fix(measurements): read displayName in SplineROI and PlanarFreehandROI reports ([PR#5908](https://github.com/OHIF/Viewers/pull/5908))
* fix(measurement): Restore viewport interactivity when deleting in-progress Spline or Livewire measurement ([PR#5905](https://github.com/OHIF/Viewers/pull/5905))
* fix(segmentation): restrict overlay segmentation menu to same frame of reference as viewport background display set ([PR#5900](https://github.com/OHIF/Viewers/pull/5900))
* fix(security): Bump flattened version to address CVE-2026-32141. ([PR#5897](https://github.com/OHIF/Viewers/pull/5897))
* fix(security): Bump tar version to address CVE-2026-31802. ([PR#5893](https://github.com/OHIF/Viewers/pull/5893))
* fix(sr-hydration): enable hydration and arrow navigation for 3D SR measurements ([PR#5887](https://github.com/OHIF/Viewers/pull/5887))
* fix: Modalities in study list should select starts with as primary ([PR#5886](https://github.com/OHIF/Viewers/pull/5886))
* fix(Threshold tool): Threshold tool no longer becomes deselected when the Dynamic option is selected ([PR#5884](https://github.com/OHIF/Viewers/pull/5884))
* fix(security): Bump svgo and tar to fix vulnerabilities. ([PR#5877](https://github.com/OHIF/Viewers/pull/5877))
* fix(seg hydration): auto-hydrate RT struct on second load with disableConfirmationPrompts ([PR#5875](https://github.com/OHIF/Viewers/pull/5875))
* fix(segmentation): Display "No description S:{series number} {modality}" for segmentations with no label. ([PR#5874](https://github.com/OHIF/Viewers/pull/5874))
* fix(SR): Added support for spline and live wire SR items. ([PR#5870](https://github.com/OHIF/Viewers/pull/5870))
* fix(security): Address various security vulnerabilities. ([PR#5869](https://github.com/OHIF/Viewers/pull/5869))
* fix(microscopy): rename measurement in microscopy mode ([PR#5866](https://github.com/OHIF/Viewers/pull/5866))
* fix(window level): The window level value is not displayed by default on all the viewports when selecting common/custom layout and TMTV. ([PR#5865](https://github.com/OHIF/Viewers/pull/5865))
* fix(segmentation): segment bidirectional tool error and show toast notification when no segment is drawn ([PR#5861](https://github.com/OHIF/Viewers/pull/5861))
* fix: Use newer ONNX version and load without errors ([PR#5854](https://github.com/OHIF/Viewers/pull/5854))
* fix capture not maintaining flip/rotate state ([PR#5843](https://github.com/OHIF/Viewers/pull/5843))
* fix(security):  CVE-2026-27212 patched. Various dependency updates as a result of CVE-2026-26996. ([PR#5830](https://github.com/OHIF/Viewers/pull/5830))
* fix: prevent viewer crash when opening DICOM Tag Browser from empty viewport - OHIF-2351 ([PR#5827](https://github.com/OHIF/Viewers/pull/5827))
* fix(security): Bump tar to 7.5.9 and lerna to 9.0.4 to fix CVE-2026-26960. ([PR#5824](https://github.com/OHIF/Viewers/pull/5824))
* fix: palette color 8 encoded 16 ([PR#5823](https://github.com/OHIF/Viewers/pull/5823))
* fix(ui-next): Add validation for opacity and border inputs in the segmentation panel - OHIF-2332 ([PR#5819](https://github.com/OHIF/Viewers/pull/5819))
* fix(dicom-pdf): enhance PDF loading with authentication support ([PR#5816](https://github.com/OHIF/Viewers/pull/5816))
* fix(colors): Replace hardcoded black with background color token ([PR#5815](https://github.com/OHIF/Viewers/pull/5815))
* fix(usAnnotation): Prevent error after JSON download ([PR#5814](https://github.com/OHIF/Viewers/pull/5814))
* fix: Published version should match ([PR#5813](https://github.com/OHIF/Viewers/pull/5813))
* fix(RTStruct): RTSTRUCT contours rendered on first slice for multi-frame images without IPP ([PR#5811](https://github.com/OHIF/Viewers/pull/5811))
* fix(dicom-tag-browser): Prevent long series names from overlapping - OHIF-2406 ([PR#5809](https://github.com/OHIF/Viewers/pull/5809))
* fix: Missing tag ([PR#5808](https://github.com/OHIF/Viewers/pull/5808))
* fix(microscopy): Prevent measurement clearing on viewport resize ([PR#5796](https://github.com/OHIF/Viewers/pull/5796))
* fix(security): Use a resolution for axios to address CVE-2026-25639. ([PR#5793](https://github.com/OHIF/Viewers/pull/5793))
* fix: Better handle octet stream case ([PR#5789](https://github.com/OHIF/Viewers/pull/5789))
* fix(app): display study not found message for all modes ([PR#5752](https://github.com/OHIF/Viewers/pull/5752))
* fix: Add a single version number with commit process ([PR#5746](https://github.com/OHIF/Viewers/pull/5746))
* fix(segmentation overlay): update viewport ds list upon seg delete - OHIF-2425 ([PR#5729](https://github.com/OHIF/Viewers/pull/5729))
* fix(cine): prevent crash on second view/hydration of SR series ([PR#5724](https://github.com/OHIF/Viewers/pull/5724))
* fix(colors): Replaces legacy colors with ui-next colors ([PR#5351](https://github.com/OHIF/Viewers/pull/5351))
* chore(deps): bump @cornerstonejs to 5.6.1 and @kitware/vtk.js to 36.4.1 ([PR#6147](https://github.com/OHIF/Viewers/pull/6147))
* chore(testing): Flock lock for playwright tests; Pin node version for OHIF; add more workers ([PR#6099](https://github.com/OHIF/Viewers/pull/6099))
* chore(testing): Playwright tests for contour segmentation create, update, delete  interactions ([PR#6091](https://github.com/OHIF/Viewers/pull/6091))
* chore(testing): Setup new self-hosted test runner ([PR#6067](https://github.com/OHIF/Viewers/pull/6067))
* chore: Fix netlify deploy by updating various @babel dependencies. ([PR#6049](https://github.com/OHIF/Viewers/pull/6049))
* chore: Fix netlify deploy. ([PR#6047](https://github.com/OHIF/Viewers/pull/6047))
* chore: update Cornerstone3D to v4.21.7 ([PR#5964](https://github.com/OHIF/Viewers/pull/5964))
* chore(refactor): use public appConfig getter instead of private _appConfig field ([PR#5923](https://github.com/OHIF/Viewers/pull/5923))
* chore(tests): contour segment interactions e2e tests - rename and togglevisibility ([PR#5891](https://github.com/OHIF/Viewers/pull/5891))
* chore(test): add FreehandROI test that MEASUREMENT_ADDED does not fire when clicking annotation text ([PR#5846](https://github.com/OHIF/Viewers/pull/5846))
* chore(security): Updates for running bun audit during CI and dependabot PR version update settings ([PR#5835](https://github.com/OHIF/Viewers/pull/5835))
* chore(deps): bump webpack from 5.95.0 to 5.105.0 ([PR#5795](https://github.com/OHIF/Viewers/pull/5795))
* chore(ci): move to next version [BUMP BETA] ([PR#5790](https://github.com/OHIF/Viewers/pull/5790))
* chore(deps): bump lodash from 4.17.21 to 4.17.23 - OHIF-2438 ([PR#5744](https://github.com/OHIF/Viewers/pull/5744))
* chore(deps): bump lodash-es from 4.17.21 to 4.17.23 - OHIF-2441 ([PR#5736](https://github.com/OHIF/Viewers/pull/5736))
* chore(deps): bump diff from 4.0.2 to 4.0.4 - OHIF-2449 ([PR#5731](https://github.com/OHIF/Viewers/pull/5731))
* test(core): use fake timers in Queue unit tests to stop CI flakes ([PR#6134](https://github.com/OHIF/Viewers/pull/6134))
* test: add E2E test for contour combine intersect and subtract operations ([PR#6131](https://github.com/OHIF/Viewers/pull/6131))
* test(segmentation): switch getSegmentCount to expect(rows).toHaveCount in panel specs ([PR#6105](https://github.com/OHIF/Viewers/pull/6105))
* test(contour): Add the ContourSegmentToggleLock.spec.ts test file to test contour locking ([PR#6072](https://github.com/OHIF/Viewers/pull/6072))
* test(contour): contour interactions delete segment ([PR#6069](https://github.com/OHIF/Viewers/pull/6069))
* test(contour): contour color change coverage ([PR#6042](https://github.com/OHIF/Viewers/pull/6042))
* test(contour): add test for RT contour position after viewport maximize/restore ([PR#6036](https://github.com/OHIF/Viewers/pull/6036))
* test(e2e): update Playwright screenshots and add better tests and assertions for area calculations ([PR#6022](https://github.com/OHIF/Viewers/pull/6022))
* test(segmentation): add e2e test for deleting segmentation on second reload ([PR#6013](https://github.com/OHIF/Viewers/pull/6013))
* test(e2e): replace waitForVolumeLoad with viewport render waits ([PR#6000](https://github.com/OHIF/Viewers/pull/6000))
* test( zoomIn-tool): verify horizontal flip is preserved in magnified view ([PR#5983](https://github.com/OHIF/Viewers/pull/5983))
* test(ContourSegNavigation): Add e2e tests for contour segmentation navigation ([PR#5834](https://github.com/OHIF/Viewers/pull/5834))
* docs(site): add ui-next component documentation and upgrade Docusaurus ([PR#6102](https://github.com/OHIF/Viewers/pull/6102))
* ui(ContextMenuViewport): move to ui-next with theme support ([PR#6008](https://github.com/OHIF/Viewers/pull/6008))
* revert: rename DisplaySet.frameOfReferenceUID back to FrameOfReferenceUID ([PR#5943](https://github.com/OHIF/Viewers/pull/5943))
* refactor(tests): update viewport page object usage to async and update all effected tests ([PR#5927](https://github.com/OHIF/Viewers/pull/5927))
* Update to use beta 5.0 of CS3D ([PR#5904](https://github.com/OHIF/Viewers/pull/5904))
* Add combined build ([PR#5895](https://github.com/OHIF/Viewers/pull/5895))
* Update CS3D 4.18.2 ([PR#5837](https://github.com/OHIF/Viewers/pull/5837))
* Add copy to clipboard option for capture ([PR#5720](https://github.com/OHIF/Viewers/pull/5720))
* Reformat in HP MPR ([PR#5717](https://github.com/OHIF/Viewers/pull/5717))
`;

function ReleaseNotes() {
  return (
    <Page>
      <SEO title="OHIF v3.13 | OHIF" />
      <Section className="mx-auto">
        <Title title="OHIF Viewer v3.13" secondary="July 2026" />

        <div className="mt-10">
          <Header overviewLabel="Overview" />
        </div>
        <div className="flex flex-col space-y-32 mt-12">
          <div>
            <div>
              <div id="overview" className="mt-8">
                <div className="flex justify-center">
                  <img
                    src={bento}
                    alt="OHIF Viewer 3.13 Overview"
                    className="rounded-2xl max-w-5xl w-full"
                  />
                </div>
              </div>
            </div>
          </div>
          <div id="new-features">
            <SubTitle title={"New Features"} />
            <div className="space-y-32">
              {/* Smart Scrollbar */}
              <Feature
                position="right"
                title="Smart Scrollbar"
                imageSrcs={[smartScrollbar]}
                wideImage
              >
                <p>
                  Navigating large image series just got easier. The new Smart
                  Scrollbar highlights which images in a series have finished
                  loading, so you always know how much is ready to view at a
                  glance.
                </p>
                <p>
                  By default, scrolling moves smoothly through the images that
                  are already loaded, meaning less time spent waiting on images
                  that are still downloading — with the ability to jump to any
                  slice with a click. It's especially handy for large studies or
                  slower connections, giving you a more responsive experience
                  and a clear visual sense of loading progress.
                </p>
                <p>
                  Learn more about the{" "}
                  <a
                    href="https://docs.ohif.org/components/smart-scrollbar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    Smart Scrollbar component
                  </a>{" "}
                  and see{" "}
                  <a
                    href="https://docs.ohif.org/platform/services/customization-service/viewportscrollbar/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    details on how to customize it
                  </a>
                  .
                </p>
              </Feature>

              {/* New Study List */}
              <Feature
                position="left"
                title="New Study List"
                imageSrcs={[studyList]}
                wideImage
              >
                <p>
                  OHIF now ships with an all-new Study List out of the box. It
                  improves quality of life with easier study preview and launch
                  capabilities, while retaining all of the customization and
                  flexibility of the previous version.
                </p>
                <p>
                  Check it out in our{" "}
                  <a
                    href="https://viewer.ohif.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    public demo
                  </a>{" "}
                  and see{" "}
                  <a
                    href="https://docs.ohif.org/migration-guide/3p12-to-3p13/work-list"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    our documentation
                  </a>{" "}
                  to learn more.
                </p>
              </Feature>

              {/* Appearance Themes & UI-next */}
              <Feature
                position="right"
                title="Appearance Themes & UI-next Component Library"
                imageSrcs={[appearance]}
                wideImage
              >
                <p>
                  We've completed the migration of all OHIF viewer components to
                  the{" "}
                  <a
                    href="https://docs.ohif.org/components"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    UI-next library
                  </a>
                  . The new library features a modern UI,
                  better support for adaptive and mobile environments, and a
                  simple preview function that lets you see a component in action
                  before building with it.
                </p>
                <p>
                  OHIF now also supports dynamic theming of viewer components,
                  including a theme previewer with many default and custom
                  options. See the{" "}
                  <a
                    href="https://docs.ohif.org/colors-and-theming"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    Colors & Theming
                  </a>{" "}
                  page for guidance, or see how to{" "}
                  <a
                    href="https://docs.ohif.org/platform/services/customization-service/appearance-theming/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-100"
                  >
                    enable the Appearance dialog
                  </a>
                  .
                </p>
              </Feature>

              {/* Labelmap Segmentation */}
              <Feature
                position="left"
                title="Labelmap Segmentation"
                imageSrcs={[labelmap]}
                wideImage
              >
                <p>
                  Following community feedback, we've revamped the one-click
                  segmentation tool to greatly improve performance and usability.
                  The tool now detects and applies region borders on PET with
                  greater accuracy and less delay during calculation.
                </p>
                <p>
                  The resulting ROI can be presented as a preview or
                  automatically accepted for seamless editing with other
                  labelmap segmentation tools.
                </p>
                <p>
                  We've also improved labelmap segmentation and fixed bugs across
                  the board, resulting in a smoother, less resource-intensive
                  experience overall.
                </p>
              </Feature>

              {/* Cornerstone 5.0 */}
              <Feature
                position="right"
                title="Cornerstone 5.0"
                imageSrcs={[cornerstone5]}
                centerImage
                noImageBorder
              >
                <p>
                  Cornerstone3D has received a major overhaul focused on improved
                  performance and ease of use across the board. Some of the
                  highlights include:
                </p>
                <ul className="list-disc list-outside pl-5 space-y-2">
                  <li>
                    <strong>Redesigned viewport architecture:</strong> a new
                    GenericViewport base with explicit, per-data-type render
                    paths (planar CPU/GPU, video, ECG, whole-slide imaging, and
                    3D volume), cleanly separating camera state, viewport
                    presentation, and data presentation.
                  </li>
                  <li>
                    <strong>New data-type support &amp; rendering features:</strong>{" "}
                    a dedicated ECG viewport for DICOM waveforms, volume cropping,
                    configurable 3D sampling distance, render stats, and image
                    smoothing/sharpening for stack and volume viewports.
                  </li>
                  <li>
                    <strong>Upgraded core foundation:</strong> bumped to vtk.js
                    34.x, a new single-source metadata module, and a toolchain
                    migration from yarn/bun to pnpm.
                  </li>
                  <li>
                    <strong>Guided upgrade path:</strong> codemods ship in the
                    5.x registry (including a generic-viewport transform) to
                    automate much of the migration from 4.x.
                  </li>
                </ul>
              </Feature>

              {/* FHIRcast Extension & General Improvements — side by side, no images */}
              <div className="flex items-start space-x-10 feature-mobile">
                <div className="w-1/2 flex flex-col space-y-8">
                  <h1 className="text-4xl text-blue-100">FHIRcast Extension</h1>
                  <p>
                    OHIF now has a dedicated FHIRcast mode. Built intentionally
                    as a standalone, external extension, it's a clean example of
                    how OHIF can be tailored and extended for specialized
                    workflows.
                  </p>
                  <ul className="list-disc list-outside pl-5 space-y-2">
                    <li>
                      <strong>Loads studies straight from a FHIR server:</strong>{" "}
                      connect OHIF directly to modern health-data servers and
                      pull in imaging studies, with secure sign-in, so images can
                      come from the systems your organization already uses.
                    </li>
                    <li>
                      <strong>Views ECG and waveform studies:</strong> a
                      dedicated viewport for displaying DICOM ECG and other
                      waveform recordings alongside regular imaging.
                    </li>
                    <li>
                      <strong>One-click study export:</strong> right-click to
                      download the current study as a DICOM ZIP file for easy
                      sharing or archiving.
                    </li>
                  </ul>
                  <p>
                    We've also published extensive user documentation, including
                    quick-start tutorial videos. Check it out in our{" "}
                    <a href="/modes/fhir-viewer" className="text-blue-100">
                      Modes gallery
                    </a>
                    .
                  </p>
                </div>
                <div className="w-1/2 flex flex-col space-y-8">
                  <h1 className="text-4xl text-blue-100">General Improvements</h1>
                  <ul className="list-disc list-outside pl-5 space-y-6">
                    <li>
                      OHIF has switched to pnpm as its package manager. Frozen
                      lockfiles mean installs are fully reproducible with the
                      exact dependency tree in{" "}
                      <span className="font-mono">pnpm-lock.yaml</span>, and
                      version-scoped overrides let us apply tighter, more
                      surgical security patches. This upgrade also means installs
                      are faster and lighter on disk storage!
                    </li>
                    <li>
                      The OHIF team continues to prioritize increased test
                      coverage across our libraries. We are also working on
                      reducing the cost for community members to contribute tests
                      of their own, which we will have more about in the next
                      update. If you are interested in contributing test coverage
                      to OHIF/Cornerstone, please let us know!
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div id="all-changes"></div>
        </div>
        <div>
          <SubTitle title={"All Changes"} />
          <div className="text-white">
            <MarkdownRenderer text={ChangeLog} />
          </div>
        </div>
      </Section>
    </Page>
  );
}

export default ReleaseNotes;
